<template>
  <section class="assessmentSection">
    <ProgressBar :value="15" />

    <div class="pageContent mb-12">
      <div class="pageTitle">Let’s figure out how to protect your windows</div>

      <div class="steps" ref="stepsEl">
        <!-- PROTECTION TYPE -->
        <div class="step">
          <div class="step-title">
            Do you want to protect all of your windows or only some of them?
          </div>
          <div class="step-description">
            Protecting all of your windows is the most secure option. Sometimes
            this can be costly. Alternativly identifying at risk windows and
            securing those may be enough.
          </div>
          <div class="step-options">
            <div class="step-option">
              <div
                class="step-option-text"
                :class="{ selected: protectionType === 'all' }"
                @click="setProtectionType('all')"
              >
                I want to protect all of my windows
              </div>
            </div>
            <div class="step-option">
              <div
                class="step-option-text"
                :class="{ selected: protectionType === 'some' }"
                @click="setProtectionType('some')"
              >
                I’ll protect some of my windows
              </div>
            </div>
            <div class="step-option">
              <div
                class="step-option-text"
                :class="{ selected: protectionType === 'idk' }"
                @click="setProtectionType('idk')"
              >
                I don't know
              </div>
            </div>
          </div>
        </div>
        <!-- END PROTECTION TYPE -->

        <!-- ACCESSIBLE WINDOWS -->
        <transition name="fade">
          <template v-if="accessibleWindowsVisible">
            <div class="step">
              <div class="step-title">
                How many windows are easily accessible?
              </div>
              <div class="step-description">
                Include any windows that can be easily accessed from outside.
                This means any windows within reach from the ground or within
                reach from easily climbing ontop of something.
              </div>
              <div class="step-options">
                <div class="number-input-container">
                  <NumberInput controls v-model="accessibleWindowsCount" />
                </div>
                <button
                  class="button"
                  @click="accessibleWindowsComplete = true"
                >
                  Continue
                </button>
              </div>
            </div>
          </template>
        </transition>
        <!-- END ACCESSIBLE WINDOWS -->

        <!-- OPEN WINDOWS -->
        <transition name="fade">
          <template v-if="accessibleWindowsComplete">
            <div class="step">
              <div class="step-title">Do all of these windows open?</div>
              <div class="step-description">
                Or in any other way than sliding up and down, or side to side?
              </div>
              <div class="step-options">
                <div class="step-option">
                  <div
                    class="step-option-text"
                    :class="{ selected: windowsOpen === true }"
                    @click="setWindowsOpen(true)"
                  >
                    Yes
                  </div>
                </div>
                <div class="step-option">
                  <div
                    class="step-option-text"
                    :class="{ selected: windowsOpen === false }"
                    @click="setWindowsOpen(false)"
                  >
                    No
                  </div>
                </div>
              </div>
            </div>
          </template>
        </transition>
        <!-- END OPEN WINDOWS -->

        <transition name="fade">
          <template v-if="accessibleWindowsComplete && windowsOpen === false">
            <div class="step">
              <div class="step-title">How many windows open?</div>
              <div class="step-description">
                We need to protect the ones that open.
              </div>
              <div class="step-options">
                <div class="number-input-container">
                  <NumberInput controls :max="accessibleWindowsCount" v-model="windowsOpenCount" />
                </div>
                <button
                  class="button"
                  @click="accessibleWindowCountComplete = true"
                >
                  Continue
                </button>
              </div>
            </div>
          </template>
        </transition>

        <transition name="fade">
          <template v-if="recommendationsVisible">
            <div class="step">
              <div class="step-title">
                We recommend {{ accessibleWindowsCount }} window sensors for
                these windows
              </div>
              <div class="step-description mb-6">
                Window sensors are great for detecting when a window opens.
                These window sensors are special and also can detect whe
                nsomeone tries to break the window or if the window is broken.
              </div>
              <div class="step-options">
                <EquipmentItemAdjuster
                  :item="windowSensor"
                  :quantity="recommendationCount"
                  :readonly="true"
                />
                <div class="step-option mt-4">
                  <div
                    class="step-option-text"
                    :class="{ selected: wantsItemInCart }"
                    @click="setWantsItemInCart(true)"
                  >
                    Add window sensors to cart
                  </div>
                </div>
                <div class="step-option">
                  <div
                    class="step-option-text"
                    :class="{ selected: wantsItemInCart === false }"
                    @click="setWantsItemInCart(false)"
                  >
                    Don't Add Window Sensors
                  </div>
                </div>
              </div>
            </div>
          </template>
        </transition>

        <transition name="fade">
          <template v-if="wantsItemInCart">
            <div class="step">
              <div class="step-title">
                Window sensors have been added to your cart
              </div>
              <div class="step-description">
                You can continue to review your order
              </div>
            </div>
          </template>
        </transition>

        <transition name="fade">
          <template v-if="contactVisible">
            <div class="step">
              <div class="step-title">
                Let’s get you connected with a security specialist.
              </div>
              <div class="step-description">
                One of our security guides can help you customize your system
                and save you money. It’ll only take a couple minutes!
              </div>
              <div class="step-options">
                <div class="step-option">
                  <div
                    class="step-option-text"
                    :class="{ selected: contactPreference === 'chat' }"
                    @click="setContactPreference('chat')"
                  >
                    Live Chat
                  </div>
                </div>
                <div class="step-option">
                  <div
                    class="step-option-text"
                    :class="{ selected: contactPreference === 'phone' }"
                    @click="setContactPreference('phone')"
                  >
                    Request a call
                  </div>
                </div>
              </div>
              <div class="flex mt-6">
                <div class="mr-2">
                  <i class="fa fa-phone text-blue"></i>
                </div>
                <div>
                  <div class="text-bold text-blue">Want to call us?</div>
                  <div>Call in at (888) 279-1409</div>
                </div>
              </div>
            </div>
          </template>
        </transition>

        <transition name="fade">
          <template v-if="contactPhoneVisible">
            <div class="step">
              <div class="step-title">What number can we reach you at?</div>
              <div class="step-description">
                One of our security guides will call within 24 hours and will
                help you customize your system and save you money. It’ll only
                take a couple minutes!
              </div>
              <div v-if="!callRequestSubmitted">
                <div class="mt-6">
                  <FormInput
                    placeholder="What is your name?"
                    type="text"
                    :value="lead.name"
                    leadKey="name"
                  />
                  <FormInput
                    placeholder="What's your phone number?"
                    type="tel"
                    mask="(###) ###-####"
                    :value="lead.phone"
                    leadKey="phone"
                  />
                </div>
                <label for="phoneConsent" ref="consentText">
                  <input
                    id="phoneConsent"
                    type="checkbox"
                    class="form-control"
                    :checked="agreedToTerms"
                    @input="(e) => handleConsent(e)"
                  />
                  I agree to give Truesecurity consent to call me at the number
                  provided.
                </label>
                <div v-if="lead.phone && agreedToTerms" class="mt-4">
                  <button type="button" class="button" @click="callMe">
                    <i class="fa fa-phone mr-1"></i>
                    Call Me
                  </button>
                </div>
              </div>
              <div v-if="callRequestSubmitted" class="pb-6">
                <div class="bg-blue text-white p-3 rounded mt-6">
                  You call request has been submitted!
                </div>
              </div>
            </div>
          </template>
        </transition>
      </div>
    </div>

    <div class="continue-section" v-if="canContinue">
      <continue-button text="Continue" :to="continueTo" />
      <ModuleNeedHelp />
    </div>
  </section>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ProgressBar from "../components/ProgressBar.vue";
import EquipmentInfoWithQuantity from "../components/EquipmentInfoWithQuantity.vue";
import EquipmentItemAdjuster from "../components/EquipmentItemAdjuster.vue";
import ModuleNeedHelp from "../components/ModuleNeedHelp.vue";
import EquipmentService from "../Services/Equipment";
import NumberInput from "../components/NumberInput.vue";
import FormInput from "../components/FormInput.vue";

let Equipment = new EquipmentService();

export default {
  name: "WindowsSection",

  components: {
    ProgressBar,
    EquipmentInfoWithQuantity,
    ModuleNeedHelp,
    NumberInput,
    EquipmentItemAdjuster,
    FormInput,
  },

  data: () => ({
    descriptionVisibilities: [false, false],
    equipment: [],
    continueTo: `/system`,
    // Questions
    protectionType: null,
    accessibleWindowsCount: 1,
    accessibleWindowsComplete: false,
    accessibleWindowCountComplete: false,
    windowsOpen: null,
    windowsOpenCount: 1,
    contactPreference: null,
    phone: "",
    agreedToTerms: false,
    wantsItemInCart: null,
    callRequestSubmitted: false,
    windowSensor: null,
  }),

  methods: {
    ...mapActions(["setCartItem"]),

    setProtectionType(type) {
      this.protectionType = type;
    },

    setWindowsOpen(value) {
      this.windowsOpen = value;
    },

    setContactPreference(value) {
      this.contactPreference = value;
      if (value === "chat") {
        this.$store.dispatch("showChat");
      }
    },

    setWantsItemInCart(value) {
      this.wantsItemInCart = value;

      let item = windowSensor;
      
      let quantity = 0;

      if (value && this.windowsOpen) {
        quantity = this.accessibleWindowsCount
      }

      if (value && !this.windowsOpen) {
        quantity = this.windowsOpenCount
      }

      // Add to cart
      this.setCartItem({
        id: item.id,
        price: item.price,
        quantity: quantity,
      });
    },

    handleConsent(e) {
      this.agreedToTerms = e.target.checked;
    },

    callMe() {
      let data = {
        name: this.lead.name,
        phone: this.lead.phone,
        consentText: this.$refs.consentText.textContent,
      };
      this.callRequestSubmitted = true;
      axios
        .post("/ajax/ticket/talk-with-guide-request", data)
        .then((res) => {
          axios.post(
            `/api/talkWithGuideRequest/${res.data.leadId}/${res.data.consentId}`
          );
          mixpanel.track("requestedTalkwithGuide", {
            P_Guide_path: window.location.pathname,
          });

          const eventName = "WINDOW_TWG_submit";
          const eventParameters = {};
          // Do tracking
          fbq("trackCustom", eventName, eventParameters);
          gtag("event", eventName, eventParameters);
          gtag("event", "conversion", {
            send_to: "AW-879819493/DSwNCPiCgoMBEOX1w6MD",
          });
          this.callRequestSubmitted = true;
        })
        .catch((e) => {
          alert("Whoops! Something went wrong. Please try again later.");
        });
    },
  },

  computed: {
    ...mapState(["lead"]),
    accessibleWindowsVisible() {
      return ["all", "some"].includes(this.protectionType);
    },

    contactVisible() {
      let customerDoesntKnow = this.protectionType === "idk";
      let notAllWindowsOpen = this.windowsOpen === false;
      let doesntWantInCart = this.wantsItemInCart === false;

      return customerDoesntKnow || doesntWantInCart;
    },

    contactPhoneVisible() {
      return this.contactPreference === "phone";
    },

    recommendationsVisible() {
      if (this.windowsOpen === true) {
        return true;
      }

      if (this.windowsOpen === false && this.accessibleWindowCountComplete === true) {
        return true;
      }

      return false;
    },

    recommendationCount() {
      if (!this.windowsOpen) {
        return this.windowsOpenCount
      }

      return this.accessibleWindowsCount
    },

    canContinue() {
      return (
        this.wantsItemInCart ||
        this.callRequestSubmitted ||
        this.contactPreference === "chat"
      );
    },
  },

  async mounted() {
    let equipment = await Equipment.getByCategory("window");
    this.equipment = equipment;
    this.windowSensor = equipment.find((e) => e.id === 2);

    if (
      this.$store.state.lead.protectionLevel === "total" &&
      !this.$route.query["continue-to-system"]
    ) {
      this.continueTo = "/home-automation";
    }

    let stepsEl = this.$refs.stepsEl;
    console.log(
      stepsEl.addEventListener("DOMNodeInserted", (event) => {
        if (!event) return null;
        let insertedEl = event.target;
        let isStep = insertedEl.classList.value.includes("step");
        if (isStep) {
          insertedEl.scrollIntoView({ behavior: "smooth" });
        }
      })
    );
  },
};
</script>

<style lang="scss" scoped>
$step-padding-left: 43px;
$blue: #0061aa;

.steps {
  padding-bottom: 36px;
}

.step {
  padding-top: 72px;
  border-left: 1px solid $blue;
  padding-left: $step-padding-left;
  scroll-margin: 72px;
}

.step-title {
  position: relative;
  font-weight: bold;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -$step-padding-left;
    transform: translateX(-50%);
    width: 24px;
    height: 24px;
    background: #fff;
    border: 1px solid $blue;
    border-radius: 50%;
  }
}

.step-options {
  padding-top: 2rem;
}

.step-option {
  &:not(:last-of-type) {
    margin-bottom: 1rem;
  }
}

.step-option-text {
  border: 1px solid $blue;
  border-radius: 1.5em;
  padding: 0.75em 1em;
  display: inline-block;
  color: $blue;
  font-weight: bold;
  cursor: pointer;
  min-width: 150px;
  text-align: center;

  &.selected {
    background-color: $blue;
    color: #fff;
  }
}

.number-input-container {
  max-width: 166px;
  margin: 1rem;
}

.button {
  border-radius: 1.5em;
}

#phoneConsent {
  display: inline-block;
  width: auto;
  margin: 0 8px 0 0;
  transform: scale(1.2);
}
</style>

<style src="./AssessmentModule.css" scoped></style>
<style src="../../sass/components/continue-section.css" scoped></style>
